// Long-form original articles for Cosmic Sage.
// These power the public /learn section — the primary content Google AdSense
// reviewers look at when evaluating "substantial original content".
// Each article is 800+ words, written in plain English, non-promotional.

export const articles = [
  {
    slug: "what-is-numerology",
    title: "What Is Numerology? A Complete Beginner's Guide",
    description:
      "Learn how numerology turns your birth date and name into numbers that reveal personality, lucky dates and life themes. Pythagorean, Chaldean and Vedic systems explained.",
    category: "Numerology",
    readMinutes: 7,
    date: "2026-01-10",
    sections: [
      {
        heading: "A short history of counting the invisible",
        body: `Numerology is the belief that numbers carry meaning beyond their mathematical value — that
the universe speaks in a numeric language and that the digits in your birth date, your full name
and even your house address are quietly describing patterns in your life. People have been doing
this for a very long time. The oldest surviving numerology texts come from Babylon and Egypt
around 500 BCE, but the system most westerners recognise today was formalised by the Greek
philosopher Pythagoras around 530 BCE. In India, the counting of planetary vibrations (Ank
Jyotish) runs in parallel to Vedic astrology and is still taught alongside it.

What all these traditions share is a simple idea: single-digit numbers (1 through 9) each have a
personality, and compound numbers can be reduced to a single root digit by adding their parts.
Numerology is not a replacement for astrology or psychology — it is a lens, a quick way to see
the themes that keep returning in your life.`,
      },
      {
        heading: "Your Driver Number (also called Birth Number or Mulank)",
        body: `This is the single digit from the day of your birth. If you were born on the 23rd, your Driver
Number is 2 + 3 = 5. If you were born on the 4th, 13th, 22nd or 31st, your Driver Number is 4.
This number describes how you naturally move through the world. It tends to be the most visible
part of your numerological fingerprint — it is the first impression you make on people.

For example, 1s often come across as initiators and self-starters. 2s read as diplomats and
listeners. 5s read as restless travellers who need variety. The Driver Number does not define
your limits; it describes your default style.`,
      },
      {
        heading: "Your Conductor Number (Life Path / Bhagyank)",
        body: `This is the single digit obtained by reducing the full birth date — day, month and year.
Someone born on 23-05-1992 would compute 2+3+0+5+1+9+9+2 = 31, then 3+1 = 4. The Conductor
Number describes the lessons you are here to learn and the themes you keep running into. If the
Driver is who you appear to be, the Conductor is who you grow into.

Where Driver and Conductor agree, life tends to feel natural. Where they disagree, there is
useful friction — the kind that forces growth.`,
      },
      {
        heading: "Pythagorean vs Chaldean Name Numerology",
        body: `Your full birth name can also be reduced to a number by assigning each letter a value.
Pythagorean numerology (the modern western system) assigns A=1, B=2, C=3, up to I=9, then
restarts at J=1. Chaldean numerology (the older Babylonian system) uses a different mapping and
deliberately skips the number 9 because it was considered sacred. The two systems often give
different results, and both are considered valid; Chaldean is generally the one preferred by
Indian numerologists.

Three numbers come out of your name: the Expression number (full name), the Soul Urge
number (vowels only — what motivates you inside) and the Personality number (consonants only
— how others see you).`,
      },
      {
        heading: "Where \"lucky numbers\" come from",
        body: `When numerologists say a number is lucky for you, they are usually looking at the harmony
between your Driver, Conductor, name number and the day's date. The traditional list of
compatible numbers between the 1 through 9 digits is well-documented in Cheiro's work and
most practitioners still refer to it today. 1 and 9, for instance, share leadership energy, while
2 and 7 share introspective energy. A number is "lucky" when it resonates with yours.

The same logic applies to car numbers, mobile numbers and house numbers — you reduce the
digits of the vehicle or address to a single digit, then check how well it matches your Driver
and Conductor.`,
      },
      {
        heading: "What numerology is not",
        body: `Numerology does not predict winning lottery tickets, exact calendar dates for life events, or
anything that stops you being responsible for your own choices. Treat it as a reflection tool —
the same way a good birthday card can nudge you to think about where you are in life. Use it
to notice patterns, pick names and dates that feel aligned, and start conversations with
yourself. If you want depth, pair it with a Vedic birth chart or a transit reading; the two
systems complement each other beautifully.

If you want to compute all four of your core numbers in under a minute, our free
numerology calculator does the full reduction for you and explains each result in plain English.`,
      },
    ],
  },

  {
    slug: "lo-shu-grid-explained",
    title: "The Lo Shu Grid Explained — How to Read Your Numerology Grid",
    description:
      "A step-by-step guide to the ancient Chinese Lo Shu numerology grid: how to build it from your date of birth, which cells matter, and what missing numbers really mean.",
    category: "Numerology",
    readMinutes: 8,
    date: "2026-01-10",
    sections: [
      {
        heading: "The 4,000-year-old square",
        body: `The Lo Shu grid is the oldest known magic square in human history. According to Chinese
legend, Emperor Yu, around 2200 BCE, saw a sacred turtle emerge from the River Lo with a 3x3
pattern of dots on its shell. The pattern had the extraordinary property that every row, every
column and both diagonals added up to 15 — the number of days in each phase of the moon.

Over centuries, this 3x3 grid became the backbone of Chinese numerology, Feng Shui direction
mapping, and even the eight trigrams of the I Ching. In modern numerology we use it to turn
your date of birth into a picture of your personality.`,
      },
      {
        heading: "Building your grid from your date of birth",
        body: `The grid has nine cells, each tied to one digit from 1 to 9. The classical layout places the
numbers in a specific pattern:

<pre>
 4 | 9 | 2
 3 | 5 | 7
 8 | 1 | 6
</pre>

To build your grid, write every digit from your full birth date into its matching cell. For
example 23-05-1992 contributes a 2, a 3, a 0 (ignored), a 5, a 1, a 9, a 9 and a 2. Your
Driver and Conductor numbers are added too. Each cell can hold multiple of the same digit —
one 2, two 9s, and so on.`,
      },
      {
        heading: "What each cell means",
        body: `Every cell is tied to a different human quality. The core meanings are:
<ul>
<li><b>1 — Expression & Communication:</b> ability to speak your mind and connect with people.</li>
<li><b>2 — Intuition & Sensitivity:</b> emotional attunement, empathy, inner knowing.</li>
<li><b>3 — Imagination & Creativity:</b> art, writing, visualisation.</li>
<li><b>4 — Discipline & Organisation:</b> structure, logic, follow-through.</li>
<li><b>5 — Freedom & Flexibility:</b> adaptability, travel, change.</li>
<li><b>6 — Love & Nurture:</b> family, service, aesthetic sense.</li>
<li><b>7 — Spirituality & Loss:</b> introspection, karmic lessons, grief.</li>
<li><b>8 — Material Success & Discipline:</b> career, finance, karma.</li>
<li><b>9 — Vitality & Fame:</b> energy, drive, global ambition.</li>
</ul>`,
      },
      {
        heading: "Planes — rows, columns and diagonals",
        body: `Any three cells in a row, column or diagonal form a "plane". A complete plane (all three
digits present at least once) means that energy flows strongly through your personality.

<ul>
<li><b>Row of 4-9-2 (Mental plane)</b> — strong intellect and planning.</li>
<li><b>Row of 3-5-7 (Emotional plane)</b> — strong feelings and sensitivity.</li>
<li><b>Row of 8-1-6 (Practical plane)</b> — strong hands and ability to execute.</li>
<li><b>Column 4-3-8 (Thought plane)</b> — a thinker who turns ideas into action.</li>
<li><b>Column 9-5-1 (Will plane)</b> — determination.</li>
<li><b>Column 2-7-6 (Action plane)</b> — service-oriented, practical.</li>
<li><b>Diagonal 4-5-6 (Prosperity plane)</b> — material comfort.</li>
<li><b>Diagonal 2-5-8 (Spiritual plane)</b> — philosophy, teaching.</li>
</ul>`,
      },
      {
        heading: "Missing numbers — the famous blind spots",
        body: `A missing cell in your grid is not a weakness; it is an invitation. It is the quality you came
into this life to develop. A missing 1, for instance, is often associated with people who have
to work hard at self-expression but, once mastered, become unusually good communicators. A
missing 4 suggests the lesson of discipline is the one your life keeps teaching you.

Some numerologists recommend remedies: wearing a specific colour, writing the missing number
daily, choosing a vehicle or phone number containing it, or placing an object in the matching
Feng Shui direction. These remedies are personal preference — they only work if you treat
them as reminders rather than guarantees.`,
      },
      {
        heading: "Reading your own Lo Shu grid",
        body: `A good first read goes like this: look for your strongest cell (the one with the most
repeats), look for your weakest or missing cell, then look at which plane is fully present and
which plane is broken. Together these four observations usually explain where your natural
confidence sits and where you tend to struggle.

Our free Lo Shu Grid tool draws the grid for you, highlights the planes, and tells you which
numbers are missing — in under a minute. It is a good way to validate your own reading.`,
      },
    ],
  },

  {
    slug: "vimshottari-dasha-guide",
    title: "Vimshottari Dasha: How Planetary Periods Shape Your Life",
    description:
      "A plain-English guide to Vimshottari Dasha — Vedic astrology's 120-year timeline of planetary periods, how each Mahadasha feels, and how Antardasha sub-periods refine the timing.",
    category: "Vedic Astrology",
    readMinutes: 9,
    date: "2026-01-11",
    sections: [
      {
        heading: "A clock built from Nakshatras",
        body: `Vimshottari Dasha is Vedic astrology's answer to the question that every birth-chart owner
eventually asks: "When?" A birth chart tells you what themes your life carries. Vimshottari
Dasha tells you when each theme is active.

The system was codified by the sage Parashara around 300 CE and runs on a 120-year wheel.
The order and length of each planetary period is fixed:
<ul>
<li>Ketu — 7 years</li>
<li>Venus — 20 years</li>
<li>Sun — 6 years</li>
<li>Moon — 10 years</li>
<li>Mars — 7 years</li>
<li>Rahu — 18 years</li>
<li>Jupiter — 16 years</li>
<li>Saturn — 19 years</li>
<li>Mercury — 17 years</li>
</ul>
Where your Dasha starts depends on the Nakshatra occupied by your Moon at the moment of
birth. Each Nakshatra is ruled by one of these nine planets; whichever Nakshatra your Moon is
in sets the starting Mahadasha of your life.`,
      },
      {
        heading: "What a Mahadasha actually feels like",
        body: `A Mahadasha is the main theme of a chapter of your life. During Saturn Mahadasha, the
19-year chapter will revolve around work, responsibility, discipline and lessons about limits.
During Venus Mahadasha, the 20-year chapter will revolve around partnerships, aesthetics,
pleasure and relationships. The quality of the Mahadasha depends on where that planet sits in
your natal chart — a well-placed Saturn gives a hard but ultimately productive 19 years; a
poorly-placed Saturn makes the same 19 years feel heavier.

The planet is not good or bad in itself. The planet is a lens that colours those 19 years.`,
      },
      {
        heading: "Antardasha — sub-periods that refine the timing",
        body: `Inside each Mahadasha are nine sub-periods called Antardashas, each ruled by one of the
nine planets. The sub-periods run in the same Vimshottari order, scaled proportionally. So
inside a 19-year Saturn Mahadasha you have Saturn-Saturn (about 3 years), Saturn-Mercury
(about 2.7 years), Saturn-Ketu (about 1.1 years), and so on.

An Antardasha is where real-world timing happens. If Saturn is your Mahadasha lord and
Jupiter is your 9th-house lord sitting in the 5th house, then Saturn-Jupiter is often the sub-
period when education, teaching or a child-related milestone happens. Vedic astrologers spend
most of their time here — not predicting that something will happen, but predicting when the
theme already in the chart becomes active.`,
      },
      {
        heading: "Pratyantar — the third level of resolution",
        body: `Each Antardasha is further divided into nine Pratyantar periods. These are the week-to-
month level sub-sub-periods and are useful for pinpointing timing to within a few weeks. A
good astrologer uses Pratyantar to answer "when exactly in this year will this result show
up?" rather than "will it show up this decade?"

Beyond Pratyantar, there are two more levels — Sookshma and Praana — which get down to
days and even hours. In practice, most readings stop at Pratyantar and switch to transits
(Gochar) for finer timing.`,
      },
      {
        heading: "Reading a Dasha with a chart",
        body: `A Dasha reading is useful only when it respects the natal chart. Here is the standard
sequence that professional Jyotishis use:

<ol>
<li>Identify the Mahadasha lord's position (house and sign).</li>
<li>Note which houses the Mahadasha lord rules.</li>
<li>Find the Antardasha lord and repeat the same two checks.</li>
<li>Check how the two planets interact — mutual aspect, mutual exchange, conjunction, etc.</li>
<li>Compare with transits — the current Dasha only "fires" when transiting planets activate
the natal position.</li>
</ol>

The result is a theme-plus-timing statement, something like: "Your Venus Mahadasha from
2026 to 2046 is a long chapter of relationship and creative themes; the Mercury sub-period
in 2029-2032 is when the writing or communication-based opportunity is most likely to open."`,
      },
      {
        heading: "A note on fear",
        body: `Reading your Dasha should not be an exercise in fear. Every planet has a role; every chapter
of 7 to 20 years has gifts and lessons. The Saturn-Saturn period that scares people in
advance often turns out to be the period they later look back on as the one that forced them
to grow up. The planets are time, nothing more. Our free Birth Chart tool computes your full
Vimshottari Dasha timeline — including every Antardasha for the next 10 years — in under a
minute.`,
      },
    ],
  },

  {
    slug: "kundli-matching-guna-milan",
    title: "Kundli Matching & Guna Milan — The Complete Guide",
    description:
      "How Vedic matchmaking really works. Learn the 8 Kootas (Varna, Vashya, Tara, Yoni, Graha Maitri, Gana, Bhakoot, Nadi), how to read scores and the common objections.",
    category: "Vedic Astrology",
    readMinutes: 10,
    date: "2026-01-12",
    sections: [
      {
        heading: "What exactly is being matched",
        body: `In Vedic astrology, kundli matching (also called Ashtakoota Milan or Guna Milan) is a
scoring system that compares two birth charts to measure how harmoniously the two people
are likely to live together. The scoring is based on the position of the Moon's Nakshatra and
sign in each chart — not the Sun sign that modern Western astrology uses.

Each chart gets scored across eight dimensions (the "ashta-kootas") worth a total of 36 points.
Traditionally, a match of 18 points or more is considered acceptable; 24 and above is
considered good; 32 and above is considered excellent. But as we will see, the raw score is
only part of the picture.`,
      },
      {
        heading: "The 8 Kootas — one by one",
        body: `<b>1. Varna (1 point) — spiritual compatibility.</b> Measures how aligned the two souls are in
their developmental stage. A high Varna indicates easy spiritual rapport.

<b>2. Vashya (2 points) — control & attraction.</b> Who holds the attracting force in the
relationship. Not about dominance; about mutual pull.

<b>3. Tara (3 points) — well-being & health.</b> Based on the Nakshatra positions of both Moons,
this koota measures prosperity and health of the marriage.

<b>4. Yoni (4 points) — sexual & emotional compatibility.</b> Each Nakshatra is assigned an
animal; some pairs are naturally friends, some are enemies.

<b>5. Graha Maitri (5 points) — mental harmony.</b> Based on the friendship between the lords of
both Moon signs. The highest-weighted intellectual compatibility factor.

<b>6. Gana (6 points) — temperament.</b> Classifies each person as Deva (divine), Manushya
(human) or Rakshasa (demonic). Does not mean one is bad — just that two Rakshasa temperaments
living together can be stormy.

<b>7. Bhakoot (7 points) — overall energy of the household.</b> Looks at the distance between the
two Moon signs. Certain positions (like 6-8 or 2-12) are considered challenging.

<b>8. Nadi (8 points) — health of children & genetic compatibility.</b> The highest-weighted
koota. If both charts have the same Nadi, this is traditionally called "Nadi Dosha" and a zero
is scored here.`,
      },
      {
        heading: "Common doshas and how they are read",
        body: `A dosha is a technical blemish that reduces an otherwise good score. The three most common
ones:

<b>Mangal Dosha.</b> Occurs when Mars sits in the 1st, 2nd, 4th, 7th, 8th or 12th house. Said
to create friction in marriage. The usual remedy is to match a Manglik with another Manglik,
which cancels the dosha.

<b>Nadi Dosha.</b> Both bride and groom have the same Nadi. Traditionally very serious, but
cancels if both share the same Moon sign or Moon Nakshatra, or if either is from a
different Rashi.

<b>Bhakoot Dosha.</b> Certain Moon-sign distances between the two charts. Cancellations exist
when the lords of both Moon signs are friends, or when the pair shares the same Nakshatra lord.

In classical Jyotish, every dosha has multiple cancellation rules. A good astrologer never
rejects a match on a single dosha without checking all cancellations.`,
      },
      {
        heading: "Why the score alone is not enough",
        body: `Two charts can score 32/36 and still produce a difficult marriage, while two charts scoring
20/36 can produce decades of happiness. The reason is that Guna Milan only checks the Moon
positions — it says nothing about the 7th house (marriage), the Lagna (overall personality),
the position of Venus (romance), or the running Dasha of each person at the time of marriage.

A proper Vedic matchmaking consultation checks all of these:
<ul>
<li>Guna Milan score (36 points)</li>
<li>Both 7th houses and their lords</li>
<li>Both Venus and Jupiter positions</li>
<li>Dasha of both partners at the time of marriage</li>
<li>Any shared doshas and their cancellations</li>
</ul>

Only after all five are read is a yes/no judgement reasonable.`,
      },
      {
        heading: "Modern perspective",
        body: `Guna Milan is a 2,000-year-old statistical system built on observed patterns. Treat it as a
compatibility checklist, not a verdict. The healthiest way to use it today is as a
conversation starter: it tells you where your temperaments are naturally aligned, and where
you will need to communicate harder. A low score does not mean "do not marry"; it means
"you will have to put real effort into these three areas". A high score does not mean
"nothing can go wrong"; it means "you have an easier starting position".

Our free Kundli Matching tool computes the full 8-koota score, flags any doshas, and gives
you a plain-English summary of what the score actually means for your relationship.`,
      },
    ],
  },

  {
    slug: "daily-panchang-explained",
    title: "Daily Panchang: Tithi, Nakshatra, Yoga, Karana & Vaar Explained",
    description:
      "Understand the five limbs of the Vedic daily calendar — Tithi, Nakshatra, Yoga, Karana and Vaar — and how to use them to pick auspicious times for everyday life.",
    category: "Vedic Astrology",
    readMinutes: 7,
    date: "2026-01-13",
    sections: [
      {
        heading: "What \"Panchang\" literally means",
        body: `Panchang comes from the Sanskrit "panch" (five) and "ang" (limb). It is the five-limbed
Vedic calendar used every single day across India to decide when to begin new ventures, when
to fast, when to travel, when to marry, when to buy gold and when to simply rest. The five
limbs are: Tithi (lunar day), Vaar (weekday), Nakshatra (lunar mansion), Yoga (sun-moon
angular combination) and Karana (half-tithi).

Together these five numbers describe the energetic quality of any given 24-hour period. A
Panchang is not just a calendar — it is a mood-reading of the day.`,
      },
      {
        heading: "Tithi — the lunar day",
        body: `A Tithi is the time it takes the Moon to move 12 degrees away from the Sun. Each lunar
month has 30 Tithis — 15 in the waxing fortnight (Shukla Paksha) ending in Purnima (full
moon), and 15 in the waning fortnight (Krishna Paksha) ending in Amavasya (new moon).

Each Tithi has a mood. Pratipada (1st) is for beginnings. Dwitiya (2nd) is for travel.
Chaturthi (4th) is for Ganesh worship. Ekadashi (11th) is for fasting. Purnima is for
celebration and donation; Amavasya is for ancestral rites and introspection. The reason
Hindus pay close attention to the lunar calendar is not superstition — it is that the Moon's
phase correlates with the collective emotional temperature.`,
      },
      {
        heading: "Vaar — the weekday",
        body: `Each weekday is ruled by a planet. Sunday = Sun, Monday = Moon, Tuesday = Mars, Wednesday
= Mercury, Thursday = Jupiter, Friday = Venus, Saturday = Saturn. If you are starting a
business, Thursday is a classic auspicious choice (Jupiter brings growth); if you are starting
a healing or counselling practice, Monday is often picked (Moon brings nourishment).`,
      },
      {
        heading: "Nakshatra — the lunar mansion",
        body: `The Vedic zodiac is divided into 27 Nakshatras, each about 13 degrees 20 minutes wide.
The Moon takes about one day to pass through each Nakshatra, so the daily Nakshatra tells
you the quality of consciousness colouring the day.

Each Nakshatra has a ruling planet, a presiding deity, a symbol and a classical "nature"
(auspicious, mixed or inauspicious). For example, Rohini (ruled by Moon) is one of the most
auspicious Nakshatras and is a classical choice for marriages; Jyeshtha (ruled by Mercury)
is considered intense and better for research than for social launches.`,
      },
      {
        heading: "Yoga — the solar-lunar angle",
        body: `Yoga is calculated from the combined positions of the Sun and Moon. There are 27 Yogas,
each lasting roughly a day. Some Yogas are classical "good" (Siddhi Yoga, Shubha Yoga,
Harshana Yoga) and are chosen for major undertakings. Others (Vyatipata, Vaidhriti) are
considered inauspicious and are typically avoided for important beginnings.

Yoga is one of the subtler limbs of Panchang and only becomes meaningful when combined with
the other four — rarely do practitioners cancel a plan on Yoga alone.`,
      },
      {
        heading: "Karana — half of a Tithi",
        body: `Each Tithi is split into two Karanas. There are seven moving Karanas (Bava, Balava,
Kaulava, Taitila, Gara, Vanija, Vishti) and four fixed Karanas (Shakuni, Chatushpada, Naga,
Kimstughna). Of these, Vishti (also called Bhadra) is the one to avoid — major undertakings
are not started during Vishti because it is considered friction-prone.`,
      },
      {
        heading: "How to actually use the Panchang",
        body: `You do not need to be an astrologer to use the Panchang. The simplest practical use is this:
before starting something that matters — a new job, a marriage, a surgery, a vehicle purchase
— check that the day's Tithi, Nakshatra and Yoga are not in the classically unfavourable list,
and that the Karana is not Vishti. If three out of five are supportive, it is a reasonable day.
If four or five are supportive, it is a strong day.

Our free Panchang page shows all five limbs for today and the next six days, highlighting
auspicious choices in gold and friction windows in red.`,
      },
    ],
  },

  {
    slug: "vastu-shastra-basics",
    title: "Vastu Shastra Basics: 16 Directions & the Energy of Your Home",
    description:
      "A beginner's guide to Vastu Shastra — the Vedic science of architecture. Learn the eight primary and eight secondary directions, which rooms belong where and simple corrections you can apply.",
    category: "Vastu",
    readMinutes: 8,
    date: "2026-01-14",
    sections: [
      {
        heading: "What Vastu Shastra actually is",
        body: `Vastu Shastra is the Vedic architectural tradition that treats a building as a living
energetic system. It assumes that every direction has an elemental quality (fire, water, earth,
air, space) and a presiding deity, and that the way you place rooms, doors, mirrors and
heavy objects inside those directions affects the quality of life for the people who live
there.

The core Vastu grid is a 9x9 square called the Vastu Purusha Mandala — the body of the
Vastu Purusha, a cosmic being who was pinned face-down into the earth by the gods and now
"lives" under every building. His head sits in the north-east and his feet point to the
south-west. His navel — the Brahmasthana — sits at the exact centre of the plot.`,
      },
      {
        heading: "The eight primary and eight secondary directions",
        body: `A traditional Vastu reading uses eight primary directions (the four cardinals and four
corners) and eight secondary directions (the midpoints between them), giving 16 total slices
of 22.5 degrees each. Each slice has a dominant element and a classical use.

<ul>
<li><b>North (Kuber):</b> wealth and finances. Keep this zone clean, light, and open.</li>
<li><b>North-East (Ishaan):</b> the holiest zone. Water, pooja room, meditation space.</li>
<li><b>East (Indra):</b> social life and growth. Main entrance is auspicious here.</li>
<li><b>South-East (Agni):</b> fire. Kitchen belongs here.</li>
<li><b>South (Yama):</b> fame and endurance. Heavy storage, no sleeping heads to south.</li>
<li><b>South-West (Nairutya):</b> stability. Master bedroom and heavy almirah belong here.</li>
<li><b>West (Varun):</b> gains. Dining and children's bedrooms work here.</li>
<li><b>North-West (Vayu):</b> movement. Guest room, kitchen (second-best), vehicles.</li>
</ul>

The secondary directions (NNE, ENE, ESE, SSE, SSW, WSW, WNW, NNW) each inherit the quality
of the two primaries they sit between.`,
      },
      {
        heading: "The Brahmasthana — the centre",
        body: `The exact geometric centre of your home is the Brahmasthana. Classical texts say it should
be left completely open — no heavy furniture, no beams, no toilets, no staircase. If your
staircase or toilet sits in the Brahmasthana, this is one of the most-cited Vastu defects.

Modern homes with open-plan living rooms often get this right by accident — a sofa island
in the middle is far better than a wall in the middle.`,
      },
      {
        heading: "Reading a floor plan with a compass",
        body: `To do a basic Vastu reading of your own home, you need two things: an accurate floor plan
drawn to scale, and the direction of magnetic north from the exact centre of the plot.

<ol>
<li>Mark the geometric centre of your plot.</li>
<li>Draw lines from the centre to the four cardinal directions (N, E, S, W) using a compass.</li>
<li>Split each quadrant into two to get the eight corners (NE, SE, SW, NW).</li>
<li>Split again to get the 16 directions.</li>
<li>Note which room sits in each slice. Compare against the ideal placement list above.</li>
</ol>

Most practical Vastu problems can be identified from this exercise alone.`,
      },
      {
        heading: "Common defects and gentle corrections",
        body: `A Vastu defect is any room, feature or heavy object placed in a direction it should not be.
Common examples:

<ul>
<li>Kitchen in the north-east (water quadrant). Correction: shift gas to south-east; use crystal lamps.</li>
<li>Master bedroom in the north-east. Correction: shift master bedroom to south-west.</li>
<li>Toilet in the north-east or Brahmasthana. Correction: keep lid down, salt bowl near, visually block it.</li>
<li>Main entrance in the south-west. Correction: use a Vastu Dosh Nivaran yantra, keep the entrance well-lit and clutter-free.</li>
<li>Beam over the bed. Correction: box the beam, or move the bed to avoid the shadow.</li>
</ul>

Most Vedic practitioners today recommend corrections over demolition. The home can almost
always be rebalanced with mirrors, salt bowls, lighting, plants, yantras and small shifts in
furniture.`,
      },
      {
        heading: "A gentle warning",
        body: `Not every single Vastu rule applies to every single home. Vastu was developed for
standalone north-facing houses on open plots. Modern flats, apartments and odd-shaped plots
need judgement. If a rule asks you to break down a wall to move a kitchen, you should
almost certainly use a correction instead.

Our free Vastu Analyzer page lets you upload your floor plan, drag the compass to true
north, choose the exact centre, and get a 16-direction AI analysis of your home in under a
minute.`,
      },
    ],
  },

  {
    slug: "name-numerology-pythagorean-chaldean",
    title: "Name Numerology: Pythagorean vs Chaldean — Which System Should You Use?",
    description:
      "A side-by-side guide to the two main name numerology systems. Learn how each one assigns values, what the Expression, Soul Urge and Personality numbers mean, and when to pick which.",
    category: "Numerology",
    readMinutes: 8,
    date: "2026-01-15",
    sections: [
      {
        heading: "Why names have numbers",
        body: `Numerology treats a name as a series of vibrations. Each letter is assigned a number, and
the sum of those numbers reduced to a single digit becomes your "name number". The
underlying assumption is that the sound and shape of a word carries energy — the same
reason mantras are repeated in Sanskrit without translation.

Two main systems dominate modern name numerology: Pythagorean and Chaldean. Both work. Both
have passionate advocates. Which one you use largely comes down to what tradition you
follow.`,
      },
      {
        heading: "Pythagorean name numerology",
        body: `Pythagorean numerology is the modern western system. It uses a simple A-Z to 1-9 mapping
that restarts every nine letters:

<pre>
1  2  3  4  5  6  7  8  9
A  B  C  D  E  F  G  H  I
J  K  L  M  N  O  P  Q  R
S  T  U  V  W  X  Y  Z
</pre>

Easy to compute by hand. The Pythagorean system is forgiving — any name reduces cleanly and
most practitioners use the first and last name together without worrying about middle names.

Pythagorean produces three numbers from your name: the Expression number (sum of all
letters), the Soul Urge number (sum of vowels — your inner driver), and the Personality
number (sum of consonants — how people perceive you).`,
      },
      {
        heading: "Chaldean name numerology",
        body: `Chaldean numerology is older — dating back to the Chaldean civilization of ancient Babylon
around 1800 BCE. The key difference is that Chaldean skips the number 9 for letters,
reserving it for sacred use. The mapping is based on the sound of the letter rather than
alphabetical order:

<pre>
1 = A, I, J, Q, Y
2 = B, K, R
3 = C, G, L, S
4 = D, M, T
5 = E, H, N, X
6 = U, V, W
7 = O, Z
8 = F, P
(no letter is assigned the value 9)
</pre>

Chaldean is traditionally the system of choice among Indian numerologists. It is considered
more accurate because it respects the sound-value of each letter, but it is also fussier —
spelling matters a lot, and small variations (Rajiv vs Rajeev) can produce different numbers.`,
      },
      {
        heading: "Three numbers that come from your name",
        body: `Regardless of system, name numerology produces three signature numbers:

<b>Expression Number</b> — the sum of all letters, reduced to a single digit. This is who you
are at the social level: how you present yourself to the world, your natural talents, the
career direction you fit into.

<b>Soul Urge Number</b> — the sum of vowels only, reduced. This is who you are at the inner
level: what you actually want from life, what feeds you emotionally.

<b>Personality Number</b> — the sum of consonants only, reduced. This is how others perceive
you before they get to know you: the first impression, the vibe.

The relationship between these three tells you how aligned your public self is with your
inner self. When Expression and Soul Urge match, you tend to pursue what actually makes
you happy. When they clash, you often end up in careers that look right on paper but feel
hollow.`,
      },
      {
        heading: "What single-digit name numbers mean",
        body: `<ul>
<li><b>1:</b> independent, leader, initiator.</li>
<li><b>2:</b> diplomatic, cooperative, sensitive.</li>
<li><b>3:</b> creative, expressive, social.</li>
<li><b>4:</b> stable, hardworking, structured.</li>
<li><b>5:</b> adventurous, restless, free-spirited.</li>
<li><b>6:</b> nurturing, home-and-family, aesthetic.</li>
<li><b>7:</b> introspective, spiritual, intellectual.</li>
<li><b>8:</b> ambitious, powerful, materially-focused.</li>
<li><b>9:</b> humanitarian, visionary, idealistic.</li>
</ul>

Master numbers 11, 22 and 33 are special — they are not reduced further, because their
intensified vibration is part of the message.`,
      },
      {
        heading: "Should you change your name?",
        body: `A common question is whether to spell your name differently to improve its number. Some
Indian numerologists specialise in exactly this — "Mahesh" becomes "Maheshh", "Ritu" becomes
"Rittu", and so on.

Our opinion: name corrections are useful when the current spelling produces a number that
clashes hard with your Driver and Conductor. But a name change is also a public identity
change — new signatures, new ID, people asking why. If you do it, do it fully, use the new
name everywhere consistently, and give it at least a year before deciding whether it worked.

Our free Name Numerology calculator supports both Pythagorean and Chaldean systems and
computes all three numbers from your current name in seconds.`,
      },
    ],
  },

  {
    slug: "palmistry-samudrik-shastra",
    title: "Palmistry Basics: The Lines, Mounts & Shapes of Samudrik Shastra",
    description:
      "A beginner's introduction to Vedic palmistry (Samudrik Shastra). Learn the four main lines, seven mounts, hand shapes and finger-based personality markers.",
    category: "Palmistry",
    readMinutes: 8,
    date: "2026-01-16",
    sections: [
      {
        heading: "A short history of reading hands",
        body: `Samudrik Shastra is the Vedic science of body-reading, and palmistry (Hast Rekha Shastra)
is its most popular branch. Classical references date back to the Mahabharata and the sage
Bhrigu's palm-leaf archives from roughly 500 BCE. The west has its own parallel tradition
dating back to Aristotle, who wrote a treatise on palmistry around 350 BCE.

The core idea is simple: the hand is a map of the mind. The way the fingers are shaped, the
way the lines are carved, the softness or hardness of the palm — all of these develop in
step with the person, and so they reflect personality, aptitude and timing.`,
      },
      {
        heading: "Hand shape — the first thing to read",
        body: `Before you look at any line, look at the hand itself. Classical palmistry classifies hands
into four elements:

<ul>
<li><b>Earth hand</b> (square palm, short fingers) — practical, grounded, reliable.</li>
<li><b>Air hand</b> (square palm, long fingers) — intellectual, curious, communicative.</li>
<li><b>Water hand</b> (long palm, long fingers) — emotional, intuitive, artistic.</li>
<li><b>Fire hand</b> (long palm, short fingers) — energetic, impulsive, charismatic.</li>
</ul>

Hand shape is the single biggest predictor of overall temperament and the frame within
which the lines operate.`,
      },
      {
        heading: "The four main lines",
        body: `<b>Life Line.</b> Curves around the thumb from between the thumb and index finger down
towards the wrist. Reads vitality, health and overall life force. Contrary to popular belief,
length is not a predictor of lifespan — depth, clarity and lack of breaks matter more.

<b>Head Line.</b> Starts near the Life Line and crosses the palm horizontally. Reads mindset,
intellect and decision-making style. A long clear Head Line means a decisive thinker; a
wavy Head Line suggests a flexible but sometimes scattered mind.

<b>Heart Line.</b> Starts under the little finger and curves across under the fingers. Reads
emotional style and relationship patterns. Deep clear curve means warm emotional expression;
straight thin line means guarded, cautious love.

<b>Fate Line.</b> Runs vertically from the wrist towards the middle finger. Reads career path
and sense of purpose. Not everyone has one — a missing or faint Fate Line often indicates
a self-made path with multiple career pivots.`,
      },
      {
        heading: "The seven mounts",
        body: `Each pad on the palm is called a mount and is named after a planet:

<ul>
<li><b>Mount of Jupiter</b> (base of index finger) — ambition, leadership.</li>
<li><b>Mount of Saturn</b> (base of middle finger) — discipline, structure, karma.</li>
<li><b>Mount of Sun / Apollo</b> (base of ring finger) — creativity, fame, aesthetic sense.</li>
<li><b>Mount of Mercury</b> (base of little finger) — communication, business sense.</li>
<li><b>Mount of Venus</b> (base of thumb) — love, pleasure, vitality.</li>
<li><b>Mount of Mars</b> (two sections — upper and lower on the palm's edge) — courage, grit.</li>
<li><b>Mount of Moon</b> (bottom edge of palm opposite thumb) — imagination, intuition, travel.</li>
</ul>

A well-developed mount means that planetary quality is strong in the person; a sunken mount
means it is underdeveloped. Mounts are read together with the Life/Head/Heart lines to form
a full picture.`,
      },
      {
        heading: "Fingers and the thumb",
        body: `Each finger represents one of the planetary mounts and shows how that quality is
expressed. The thumb is a special case — it is read as the willpower centre. A thumb with
a long, firm first phalange (nail joint) indicates strong will; a thumb that bends backwards
easily indicates adaptability.

Classical palmistry also reads the shape of finger tips. Spatulate (fan-shaped) tips mean
practical and inventive; conic tips mean emotional and artistic; square tips mean logical;
pointed tips mean idealistic.`,
      },
      {
        heading: "What palmistry does not do",
        body: `Palmistry does not predict the date you will die, the exact person you will marry, or
anything that removes your own agency. The lines on your hand are not fixed — they change
over the course of your life as your mindset and habits change. A good palm reader will
tell you what the lines currently suggest; the next decade is yours to redraw.

Our free AI Palm Reading tool analyses an uploaded palm image and gives you a plain-English
reading of lines, mounts and hand shape — using Vedic Samudrik Shastra as the frame.`,
      },
    ],
  },
];

export const getArticleBySlug = (slug) => articles.find((a) => a.slug === slug);
